import React, {useEffect, useState} from 'react';
import {animate, motion, useMotionValue, useTransform} from 'framer-motion';
import {ArrowDown, Camera, ChevronRight, Eye, Play} from 'lucide-react';
import {Link} from 'react-router-dom';

function Counter({target, suffix = ''}: { target: number; suffix?: string }) {
    const count = useMotionValue(0);
    const rounded = useTransform(count, (v) => `${Math.round(v)}${suffix}`);
    const [display, setDisplay] = useState(`0${suffix}`);
    useEffect(() => {
        const controls = animate(count, target, {duration: 2.5, ease: 'easeOut'});
        const unsub = rounded.on('change', (v) => setDisplay(v));
        return () => {
            controls.stop();
            unsub();
        };
    }, [target, count, rounded]);
    return <span>{display}</span>;
}

function FadeUp({children, delay = 0, className = ''}: {
    children: React.ReactNode;
    delay?: number;
    className?: string
}) {
    return (
        <motion.div
            initial={{opacity: 0, y: 30}}
            whileInView={{opacity: 1, y: 0}}
            viewport={{once: true}}
            transition={{duration: 0.7, delay, ease: [0.25, 0.4, 0.25, 1]}}
            className={className}
        >{children}</motion.div>
    );
}

// A realistic CCTV bullet camera that physically pans left↔right like a real
// surveillance camera — just the camera, no frame, no HUD, no beam.
function CameraVision() {
    const angle = useMotionValue(-30);

    useEffect(() => {
        const controls = animate(angle, [-30, 30, 30, -30, -30], {
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.42, 0.5, 0.92, 1],
        });
        return () => controls.stop();
    }, [angle]);

    const panTransform = useTransform(angle, (a) => `rotateY(${a}deg)`);

    return (
        <div className="relative w-full h-[440px] flex items-start justify-center select-none"
             style={{perspective: '1100px'}}>

            {/* Ambient glow behind the camera */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-primary/10 blur-3xl"/>

            {/* Camera assembly that pans around the mount axis */}
            <motion.div
                className="absolute top-[42px] left-1/2 -translate-x-1/2"
                style={{transform: panTransform, transformOrigin: '50% 0%', transformStyle: 'preserve-3d'}}
            >
                {/* Arm */}
                <div className="mx-auto w-[18px] h-20 rounded-b-xl"
                     style={{background: 'linear-gradient(90deg, #12161c, #3a424f 48%, #0e1217)'}}/>

                {/* Knuckle joint */}
                <div className="mx-auto -mt-2 w-9 h-9 rounded-full"
                     style={{background: 'radial-gradient(circle at 34% 30%, #5e6877, #161b22 78%)'}}/>

                {/* Bullet barrel */}
                <div className="relative -mt-2">
                    {/* Sun shield / hood, overhanging the lens */}
                    <div className="absolute -top-[10px] left-1/2 -translate-x-1/2 w-[188px] h-[60px] rounded-[34px] z-10"
                         style={{
                             background: 'linear-gradient(180deg, #4d5562, #2b323d)',
                             boxShadow: '0 8px 16px rgba(0,0,0,0.35)',
                         }}/>

                    {/* Barrel body */}
                    <div className="relative mx-auto w-[176px] h-[78px] rounded-[40px] overflow-hidden"
                         style={{
                             background: 'linear-gradient(180deg, #3d4450 0%, #2a313b 42%, #141820 100%)',
                             boxShadow:
                                 'inset 0 3px 7px rgba(255,255,255,0.14), inset 0 -8px 14px rgba(0,0,0,0.55), 0 18px 32px rgba(0,0,0,0.45)',
                         }}>
                        <div className="absolute top-1.5 left-8 right-8 h-2 rounded-full bg-white/15 blur-[2px]"/>
                        <div className="absolute top-0 bottom-0 left-[62%] w-px bg-black/30"/>
                        <div className="absolute top-0 bottom-0 left-[64%] w-px bg-white/5"/>
                    </div>

                    {/* Lens facing the viewer */}
                    <div className="absolute top-1/2 left-[60%] -translate-y-1/2 w-[78px] h-[78px] rounded-full z-20"
                         style={{
                             background: 'radial-gradient(circle at 36% 30%, #2c323b, #090b0f 70%)',
                             boxShadow: '0 0 0 5px #1c222b, 0 10px 20px rgba(0,0,0,0.5)',
                         }}>
                        <div className="absolute inset-2 rounded-full"
                             style={{background: 'radial-gradient(circle at 40% 34%, #1a2840, #04060a 72%)'}}/>
                        <motion.div className="absolute inset-[15px] rounded-full border border-primary/50"
                                    animate={{opacity: [0.4, 0.9, 0.4]}}
                                    transition={{duration: 1.6, repeat: Infinity}}/>
                        {/* glass highlight */}
                        <div className="absolute top-3.5 left-3.5 w-5 h-5 rounded-full bg-white/35 blur-[1px]"/>
                        {/* centre sensor glow */}
                        <motion.div className="absolute inset-0 m-auto w-2.5 h-2.5 rounded-full bg-primary"
                                    style={{boxShadow: '0 0 12px var(--primary)'}}
                                    animate={{opacity: [0.55, 1, 0.55]}}
                                    transition={{duration: 1.4, repeat: Infinity}}/>
                    </div>

                    {/* IR LEDs ring hint + REC led */}
                    <motion.div className="absolute top-3.5 left-6 w-1.5 h-1.5 rounded-full bg-red-500 z-20"
                                animate={{opacity: [1, 0.2, 1]}}
                                transition={{duration: 1, repeat: Infinity}}/>
                </div>
            </motion.div>
        </div>
    );
}

export function HeroSection() {
    return (
        <section id="hero"
                 className="w-full scroll-mt-16 relative overflow-hidden min-h-[100svh] flex items-center bg-background">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    className="absolute top-[15%] right-[15%] w-[min(400px,80vw)] h-[min(400px,80vw)] rounded-full bg-primary/15 dark:bg-primary/10 blur-[120px] will-change-transform"
                    animate={{scale: [1, 1.15, 1]}}
                    transition={{duration: 10, repeat: Infinity, ease: 'easeInOut'}}
                />
                <motion.div
                    className="absolute bottom-[20%] left-[10%] w-[min(300px,60vw)] h-[min(300px,60vw)] rounded-full bg-primary/10 dark:bg-primary/5 blur-[100px] will-change-transform"
                    animate={{scale: [1, 1.1, 1]}}
                    transition={{duration: 12, repeat: Infinity, ease: 'easeInOut'}}
                />
            </div>

            <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-8 py-24 md:py-0">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="flex flex-col gap-6 md:gap-8">
                        <FadeUp delay={0.15}>
                            <h1 className="text-4xl sm:text-5xl md:text-[3.5rem] lg:text-6xl font-extrabold tracking-tight leading-[1.08]">
                                Monitoring Mielec
                                <br/>
                                <span className="text-primary">Montaż kamer i CCTV</span>
                            </h1>
                        </FadeUp>

                        <FadeUp delay={0.3}>
                            <p className="text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
                                Zabezpiecz swój dom, firmę lub gospodarstwo z lokalnymi ekspertami monitoringu.
                                <strong> Siedziba w Mielcu - obsługujemy obszar w promieniu do 250 km</strong> (m.in. Rzeszów, Tarnów, Dębica, Stalowa Wola, Tarnobrzeg, Kolbuszowa, Połaniec, Sandomierz, Kielce, Kraków).
                                Kamery Ultra HD 4K, podgląd na telefonie 24/7, ekspresowy montaż i do 3 lat gwarancji.
                            </p>
                        </FadeUp>

                        <FadeUp delay={0.45} className="flex flex-wrap gap-3">
                            <Link
                                to="/wycena"
                                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary text-primary-foreground text-sm md:text-base font-semibold shadow-md hover:shadow-lg hover:brightness-110 transition-all"
                            >
                                <Camera size={18}/>
                                Bezpłatna wycena
                                <ChevronRight size={16} className="group-hover:translate-x-0.5 transition-transform"/>
                            </Link>
                            <a
                                href="#uslugi"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-border bg-background text-foreground text-sm md:text-base font-semibold hover:bg-muted transition-all"
                            >
                                <Play size={16} className="text-primary"/>
                                Poznaj usługi
                            </a>
                        </FadeUp>

                        <FadeUp delay={0.6}>
                            <div className="flex items-center gap-3 pt-2 flex-wrap">
                                <div className="flex -space-x-2">
                                    {[0, 1, 2, 3].map((i) => (
                                        <div key={i}
                                             className="w-8 h-8 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                                            <Eye size={12} className="text-primary"/>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-sm text-muted-foreground">
                                    <span className="font-bold text-foreground">500+</span> instalacji monitoringu w
                                    Polsce
                                </p>
                            </div>
                        </FadeUp>
                    </div>

                    <FadeUp delay={0.25} className="hidden lg:block">
                        <div className="relative">
                            <CameraVision/>
                        </div>
                    </FadeUp>
                </div>

                <FadeUp delay={0.7} className="mt-16 lg:mt-20">
                    <div className="grid grid-cols-3 gap-3 md:gap-6 max-w-2xl mx-auto lg:max-w-none">
                        {[
                            {value: 500, suffix: '+', label: 'Kamer zamontowanych w Polsce'},
                            {value: 8, suffix: '+', label: 'Lat doświadczenia w branży'},
                            {value: 100, suffix: '%', label: 'Zadowolonych klientów'},
                        ].map((stat, i) => (
                            <div key={i}
                                 className="text-center p-4 md:p-5 rounded-2xl bg-muted/50 border border-border">
                                <p className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-primary tabular-nums">
                                    <Counter target={stat.value} suffix={stat.suffix}/>
                                </p>
                                <p className="text-[11px] sm:text-xs md:text-sm text-muted-foreground mt-1">{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </FadeUp>

                <FadeUp delay={0.9} className="flex justify-center mt-12">
                    <a href="#uslugi"
                       className="flex flex-col items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors">
                        <span className="text-xs font-medium">Przewiń niżej</span>
                        <motion.div animate={{y: [0, 6, 0]}} transition={{duration: 1.5, repeat: Infinity}}>
                            <ArrowDown size={16}/>
                        </motion.div>
                    </a>
                </FadeUp>
            </div>
        </section>
    );
}
